/**
 * Email Activity Tab — Layer 2 of Bulletproof Email Delivery Monitoring.
 *
 * Reads from db.email_log (populated automatically by EmailService.send_email)
 * and surfaces:
 *  - Health verdict (healthy / degraded / down)
 *  - Counts in last N days (sent / failed / simulated, success-rate %)
 *  - Last successful send + last failure with SMTP response
 *  - Filterable, paginated table of every email attempt
 *  - "Trigger canary now" button (manual SMTP probe)
 *  - "Resend" action on welcome rows so admin can re-deliver to a user
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Mail, CheckCircle2, XCircle, Activity, RefreshCw, Send, AlertTriangle,
  Clock, ChevronLeft, ChevronRight, FlaskConical, Search,
} from 'lucide-react';
import { API_BASE, getAdminToken, formatDate } from './adminUtils';

const STATUS_COLOR = {
  sent: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  failed: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  simulated: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
};

const HEALTH_BADGE = {
  healthy: { label: 'Healthy', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  degraded: { label: 'Degraded', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  down: { label: 'Down', cls: 'bg-rose-500/25 text-rose-300 border-rose-500/40' },
};

export const EmailActivityTab = () => {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [recipient, setRecipient] = useState('');
  const [triggeredBy, setTriggeredBy] = useState('');
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [canaryRunning, setCanaryRunning] = useState(false);
  const [resendingFor, setResendingFor] = useState(null);
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [testEmailType, setTestEmailType] = useState('streak_risk');
  const [testEmailRunning, setTestEmailRunning] = useState(false);

  const authHeader = () => ({ Authorization: `Bearer ${getAdminToken()}` });

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/email-logs/stats?days=${days}`, { headers: authHeader() });
      if (res.ok) setStats(await res.json());
    } catch (e) {
      console.error('email stats failed', e);
    }
  }, [days]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50', days: String(days) });
      if (statusFilter) params.set('status_filter', statusFilter);
      if (recipient.trim()) params.set('recipient', recipient.trim());
      if (triggeredBy) params.set('triggered_by', triggeredBy);
      const res = await fetch(`${API_BASE}/api/admin/email-logs?${params}`, { headers: authHeader() });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setTotalPages(data.total_pages || 1);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('email logs failed', e);
    } finally {
      setLoading(false);
    }
  }, [page, days, statusFilter, recipient, triggeredBy]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const triggerCanary = async () => {
    setCanaryRunning(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/email-logs/canary`, {
        method: 'POST', headers: authHeader(),
      });
      const j = await res.json().catch(() => ({}));
      if (res.ok && j.ok) {
        toast.success('Canary email sent — check inbox in ~30s');
      } else {
        toast.error('Canary failed — check SMTP logs');
      }
      // Give server a moment to write the log row, then refresh
      setTimeout(() => { fetchStats(); fetchLogs(); }, 1200);
    } catch (e) {
      toast.error(`Canary error: ${e.message}`);
    } finally {
      setCanaryRunning(false);
    }
  };

  const resend = async (row) => {
    if (!row?.recipient) return;
    setResendingFor(row.log_id);
    try {
      const res = await fetch(`${API_BASE}/api/admin/email-logs/resend`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient: row.recipient, template: 'welcome' }),
      });
      const j = await res.json().catch(() => ({}));
      if (res.ok && j.ok) {
        toast.success(`Re-sent welcome email to ${row.recipient}`);
        setTimeout(() => { fetchStats(); fetchLogs(); }, 1200);
      } else {
        toast.error(j.detail || 'Resend failed');
      }
    } catch (e) {
      toast.error(`Resend error: ${e.message}`);
    } finally {
      setResendingFor(null);
    }
  };

  const StatCard = ({ icon: Icon, label, value, sub, color = 'blue', testid }) => {
    const palette = {
      blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/20 text-blue-300',
      emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/20 text-emerald-300',
      rose: 'from-rose-500/20 to-rose-600/10 border-rose-500/20 text-rose-300',
      amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/20 text-amber-300',
      cyan: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/20 text-cyan-300',
    }[color];
    return (
      <Card className={`bg-gradient-to-br ${palette} backdrop-blur`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs ${palette.split(' ').pop()} opacity-80`}>{label}</p>
              <p className="text-2xl font-bold text-white" data-testid={testid}>{value}</p>
              {sub && <p className="text-[11px] mt-1 text-white/60">{sub}</p>}
            </div>
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const health = stats?.health || 'healthy';
  const healthInfo = HEALTH_BADGE[health];

  return (
    <div className="space-y-6" data-testid="email-activity-tab">
      {/* Header + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Email Activity</h2>
            <p className="text-sm text-slate-400">Real-time SMTP delivery audit · last {days} days</p>
          </div>
          <Badge className={`${healthInfo.cls} border ml-2`} data-testid="email-health-badge">
            <Activity className="w-3 h-3 mr-1" /> {healthInfo.label}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="email-days-filter"
            value={days}
            onChange={(e) => { setDays(Number(e.target.value)); setPage(1); }}
            className="bg-slate-800 border border-white/10 text-white text-sm rounded-md px-3 py-2"
          >
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button
            data-testid="email-refresh-btn"
            variant="outline"
            className="border-white/10 text-white hover:bg-white/10"
            onClick={() => { fetchStats(); fetchLogs(); }}
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button
            data-testid="email-canary-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white"
            onClick={triggerCanary}
            disabled={canaryRunning}
          >
            <FlaskConical className="w-4 h-4 mr-2" />
            {canaryRunning ? 'Running…' : 'Run Canary'}
          </Button>
        </div>
      </div>

      {/* Retention Email Test Panel */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
        <p className="text-sm font-semibold text-white mb-3">🧪 Test Retention Email</p>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Send to</label>
            <input
              type="email"
              value={testEmailAddr}
              onChange={e => setTestEmailAddr(e.target.value)}
              placeholder="your@email.com"
              className="bg-slate-700 border border-slate-600 text-white text-sm rounded-md px-3 py-2 w-56"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Email type</label>
            <select
              value={testEmailType}
              onChange={e => setTestEmailType(e.target.value)}
              className="bg-slate-700 border border-slate-600 text-white text-sm rounded-md px-3 py-2"
            >
              <option value="streak_risk">Streak At Risk</option>
              <option value="almost_done">Lab Almost Done</option>
              <option value="weekly_digest">Weekly Digest</option>
            </select>
          </div>
          <Button
            className="bg-violet-600 hover:bg-violet-700 text-white"
            disabled={testEmailRunning || !testEmailAddr}
            onClick={async () => {
              setTestEmailRunning(true);
              try {
                const res = await fetch(`${API_BASE}/api/admin/retention/test-email`, {
                  method: 'POST',
                  headers: { ...authHeader(), 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: testEmailAddr, email_type: testEmailType }),
                });
                const j = await res.json().catch(() => ({}));
                if (res.ok && j.ok) {
                  toast.success(`Test email sent to ${testEmailAddr}`);
                  setTimeout(() => { fetchStats(); fetchLogs(); }, 1200);
                } else {
                  toast.error(j.error || 'Send failed — check logs');
                }
              } catch (e) {
                toast.error(`Error: ${e.message}`);
              } finally {
                setTestEmailRunning(false);
              }
            }}
          >
            {testEmailRunning ? 'Sending…' : 'Send Test Email'}
          </Button>
        </div>
      </div>

      {/* Win-back Email Test Panel */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
        <p className="text-sm font-semibold text-white mb-3">🔁 Test Win-back Email</p>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Send to</label>
            <input
              type="email"
              value={testEmailAddr}
              onChange={e => setTestEmailAddr(e.target.value)}
              placeholder="your@email.com"
              className="bg-slate-700 border border-slate-600 text-white text-sm rounded-md px-3 py-2 w-56"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Stage</label>
            <select
              id="winback-type-select"
              defaultValue="winback_d7"
              className="bg-slate-700 border border-slate-600 text-white text-sm rounded-md px-3 py-2"
            >
              <option value="winback_d7">D7 — Come Back</option>
              <option value="winback_d14">D14 — Industry Update</option>
              <option value="winback_d21">D21 — Social Proof</option>
              <option value="winback_d30">D30 — Momentum Fading</option>
              <option value="winback_d45">D45 — Last Chance</option>
            </select>
          </div>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={testEmailRunning || !testEmailAddr}
            onClick={async () => {
              const emailType = document.getElementById('winback-type-select').value;
              setTestEmailRunning(true);
              try {
                const res = await fetch(`${API_BASE}/api/admin/winback/test-email`, {
                  method: 'POST',
                  headers: { ...authHeader(), 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: testEmailAddr, email_type: emailType }),
                });
                const j = await res.json().catch(() => ({}));
                if (res.ok && j.ok) {
                  toast.success(`Win-back test sent to ${testEmailAddr}`);
                  setTimeout(() => { fetchStats(); fetchLogs(); }, 1200);
                } else {
                  toast.error(j.error || 'Send failed — check logs');
                }
              } catch (e) {
                toast.error(`Error: ${e.message}`);
              } finally {
                setTestEmailRunning(false);
              }
            }}
          >
            {testEmailRunning ? 'Sending…' : 'Send Test Email'}
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard
            icon={CheckCircle2} label="Sent" value={stats.counts?.sent ?? 0}
            sub={`${stats.success_rate ?? 0}% success`} color="emerald"
            testid="stat-emails-sent"
          />
          <StatCard
            icon={XCircle} label="Failed" value={stats.counts?.failed ?? 0}
            sub={stats.failed_24h ? `${stats.failed_24h} in 24h` : 'No recent failures'}
            color="rose" testid="stat-emails-failed"
          />
          <StatCard
            icon={AlertTriangle} label="Simulated" value={stats.counts?.simulated ?? 0}
            sub="No SMTP creds" color="amber"
          />
          <StatCard
            icon={Send} label="Last 24h sent" value={stats.sent_24h ?? 0}
            sub={stats.last_sent ? `→ ${stats.last_sent.recipient}` : '—'}
            color="cyan"
          />
          <StatCard
            icon={Clock} label="Last failure"
            value={stats.last_failed ? formatDate(stats.last_failed.sent_at) : 'None'}
            sub={stats.last_failed?.recipient || 'All clear'}
            color="rose"
          />
          <StatCard
            icon={FlaskConical} label="Last canary"
            value={stats.last_canary ? (stats.last_canary.status || '—') : 'Never'}
            sub={stats.last_canary ? formatDate(stats.last_canary.sent_at) : 'Run a probe →'}
            color="blue"
          />
        </div>
      )}

      {/* Banner for outage */}
      {stats?.health === 'down' && (
        <Card className="bg-rose-500/10 border-rose-500/30">
          <CardContent className="p-4 text-rose-200 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5" />
            <div>
              <p className="font-semibold">SMTP appears DOWN — zero successful sends in the last 24h.</p>
              <p className="text-sm opacity-80 mt-1">
                Check Microsoft 365 Security Defaults, app password validity, and tail{' '}
                <code className="bg-white/10 px-1 py-0.5 rounded">/var/log/supervisor/backend.err.log</code>.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {stats?.health === 'degraded' && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4 text-amber-200 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5" />
            <div>
              <p className="font-semibold">Delivery degraded — failure rate &gt; 25% in last 24h.</p>
              <p className="text-sm opacity-80 mt-1">
                Inspect the failed rows below. Most recent error:{' '}
                <span className="font-mono">{stats.last_failed?.smtp_response || 'n/a'}</span>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="bg-white/5 border border-white/10">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400" />
            <Input
              data-testid="email-search-recipient"
              placeholder="Search recipient email…"
              value={recipient}
              onChange={(e) => { setRecipient(e.target.value); setPage(1); }}
              className="bg-slate-800 border-white/10 text-white"
            />
          </div>
          <select
            data-testid="email-status-filter"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="bg-slate-800 border border-white/10 text-white text-sm rounded-md px-3 py-2"
          >
            <option value="">All statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="simulated">Simulated</option>
          </select>
          <select
            data-testid="email-triggered-by-filter"
            value={triggeredBy}
            onChange={(e) => { setTriggeredBy(e.target.value); setPage(1); }}
            className="bg-slate-800 border border-white/10 text-white text-sm rounded-md px-3 py-2"
          >
            <option value="">All triggers</option>
            <option value="registration">Registration</option>
            <option value="admin_notification">Admin Notification</option>
            <option value="payment_confirmation">Payment Confirmation</option>
            <option value="payment_failed">Payment Failed</option>
            <option value="course_enrollment">Course Enrollment</option>
            <option value="password_reset">Password Reset</option>
            <option value="abandoned_cart_recovery">Abandoned Cart</option>
            <option value="payment_notification">Payment Notification</option>
            <option value="job_notification">Job Notification</option>
            <option value="canary">Canary</option>
            <option value="admin_resend">Admin Resend</option>
            <option disabled>── Re-engagement ──</option>
            <option value="drip_d1">Drip D1 — Free Labs</option>
            <option value="drip_d3">Drip D3 — Getting Started</option>
            <option value="drip_d7">Drip D7 — Real Servers</option>
            <option value="drip_d14">Drip D14 — RHCSA Cert</option>
            <option value="drip_d30">Drip D30 — Last Email</option>
            <option disabled>── Retention ──</option>
            <option value="streak_risk">Streak At Risk</option>
            <option value="almost_done">Lab Almost Done</option>
            <option value="weekly_digest">Weekly Digest</option>
            <option disabled>── Win-back ──</option>
            <option value="winback_d7">Win-back D7 — Come Back</option>
            <option value="winback_d14">Win-back D14 — Industry Update</option>
            <option value="winback_d21">Win-back D21 — Social Proof</option>
            <option value="winback_d30">Win-back D30 — Momentum Fading</option>
            <option value="winback_d45">Win-back D45 — Last Chance</option>
            <option value="buyer_d1">Buyer D1 — Start Lab</option>
            <option value="buyer_d3">Buyer D3 — Keep Going</option>
            <option disabled>── Exam ──</option>
            <option value="exam_eligibility">Exam Eligibility</option>
            <option value="exam_booking">Exam Booking Confirmation</option>
            <option value="exam_booking_admin">Exam Booking Admin Alert</option>
            <option value="exam_discount_reminder">Exam Discount Reminder</option>
            <option value="exam_discount_carried_forward">Exam Discount Carried Forward</option>
          </select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white/5 border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-slate-400 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Time</th>
                <th className="text-left px-4 py-3">Recipient</th>
                <th className="text-left px-4 py-3">Subject</th>
                <th className="text-left px-4 py-3">Template</th>
                <th className="text-left px-4 py-3">Trigger</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Latency</th>
                <th className="text-left px-4 py-3">Opened</th>
                <th className="text-left px-4 py-3">Clicked</th>
                <th className="text-left px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-12 text-slate-400">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-slate-400">No email log entries in this window.</td></tr>
              ) : items.map((row) => (
                <tr key={row.log_id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{formatDate(row.sent_at)}</td>
                  <td className="px-4 py-3 text-white font-mono text-xs">{row.recipient}</td>
                  <td className="px-4 py-3 text-slate-300 max-w-[280px] truncate" title={row.subject}>{row.subject || '—'}</td>
                  <td className="px-4 py-3 text-slate-400">{row.template || '—'}</td>
                  <td className="px-4 py-3 text-slate-400">{row.triggered_by || '—'}</td>
                  <td className="px-4 py-3">
                    <Badge className={`${STATUS_COLOR[row.status] || 'bg-slate-500/20 text-slate-300'} border`}>
                      {row.status}
                    </Badge>
                    {row.status === 'failed' && row.smtp_response && (
                      <p className="text-[11px] text-rose-300/80 mt-1 max-w-xs truncate" title={row.smtp_response}>
                        {row.smtp_response}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-400">{row.duration_ms ? `${row.duration_ms} ms` : '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {row.opened
                      ? <span className="text-emerald-400 font-medium">✓</span>
                      : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {row.clicked
                      ? <span className="text-emerald-400 font-medium">✓{row.click_count > 0 ? ` (${row.click_count})` : ''}</span>
                      : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {row.status === 'failed' && row.template === 'welcome' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-white/10 text-white hover:bg-white/10 h-7"
                        onClick={() => resend(row)}
                        disabled={resendingFor === row.log_id}
                        data-testid={`resend-${row.recipient}`}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        {resendingFor === row.log_id ? '…' : 'Resend'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 text-sm text-slate-400">
            <span>Page {page} of {totalPages} · {total} total</span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-white/10 text-white hover:bg-white/10 disabled:opacity-40"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="email-page-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-white/10 text-white hover:bg-white/10 disabled:opacity-40"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                data-testid="email-page-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default EmailActivityTab;
